import { collection, getDocs, query, where } from 'firebase/firestore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArchivedWalletLogsGroup, WalletLog } from '../../../apiTypes';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { db } from '../../../firebase';
import { setDoc, useCollection } from '../../../utils/api';
import { getDayTimeRange } from '../../../utils/dates';
import getDateKey from '../../../utils/dates/getDateKey';
import getDateKeys from '../../../utils/dates/getDateKeys';
import getTimeRangeFromDateKey from '../../../utils/dates/getTimeRangeFromDateKey';

export const useGetArchivedWalletLogs = (timeRange: [number, number]) => {
  const { cafeSlug } = useAdmin();
  const { wallets } = useCafe();

  const [todayLogs, setTodayLogs] = useState<WalletLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useRef(false);

  const getLogs = useCallback(
    async (timeRange: [number, number]) => {
      setIsLoading(true);
      const newLogs: WalletLog[] = [];
      const promises = wallets.map(async (wallet) => {
        const myCollection = collection(db, 'cafes', cafeSlug, 'wallets', wallet.id, 'logs');

        /** @todo remove */
        const startDate = cafeSlug === 'lm64cv5j' && timeRange[0] < 1711494000000 ? 1711494000000 : timeRange[0];

        const documentSnapshots = await getDocs(
          query(myCollection, where('time', '>', startDate), where('time', '<', timeRange[1]))
        );
        documentSnapshots.forEach((doc) => newLogs.push(doc.data() as WalletLog));
      });
      await Promise.all(promises);
      setIsLoading(false);
      return newLogs;
    },
    [cafeSlug, wallets]
  );

  const getTodayLogs = useCallback(async () => {
    const tadayTimeRange = getDayTimeRange(Date.now());
    const logs = await getLogs(tadayTimeRange);
    setTodayLogs(logs);
  }, [getLogs]);

  const { data, isValidating } = useCollection<ArchivedWalletLogsGroup>(`cafes/${cafeSlug}/archivedWalletLogs`, {
    where: ['dateKey', '>=', getDateKey(timeRange[0])],
    where2: ['dateKey', '<=', getDateKey(timeRange[1])],
  });

  const todayDayKey = useMemo(() => getDateKey(new Date()), []);

  const dateKeys = useMemo(() => getDateKeys(timeRange), [timeRange]);

  const hasToday = useMemo(() => dateKeys.includes(todayDayKey), [dateKeys, todayDayKey]);

  const missedDateKeys = useMemo(
    () =>
      isValidating || !data
        ? []
        : dateKeys.filter((dk) => !data?.some((doc) => doc.dateKey === dk) && dk < todayDayKey),
    [data, dateKeys, isValidating, todayDayKey]
  );

  const firstMissedKey = missedDateKeys[0];

  const generateLogGroups = useCallback(
    async (dateKey: number) => {
      const timeRange = getTimeRangeFromDateKey(dateKey);

      const logs = await getLogs(timeRange);

      await setDoc<ArchivedWalletLogsGroup>(`cafes/${cafeSlug}/archivedWalletLogs/${dateKey}`, {
        dateKey,
        logs,
      });
    },
    [cafeSlug, getLogs]
  );

  const archivedLogs = useMemo(() => {
    if (firstMissedKey) {
      return [];
    }
    const archivedData = data?.map((i) => i.logs).flat() || [];
    if (hasToday) {
      return [...archivedData, ...(todayLogs || [])];
    }
    return archivedData;
  }, [data, firstMissedKey, hasToday, todayLogs]);

  useEffect(() => {
    if (mountedRef.current) {
      getTodayLogs();
    }
    mountedRef.current = true;
  }, [getTodayLogs]);

  useEffect(() => {
    if (firstMissedKey) {
      generateLogGroups(firstMissedKey);
    }
  }, [firstMissedKey, generateLogGroups]);

  return {
    logs: archivedLogs,
    isLoading: isValidating || isLoading || !!firstMissedKey,
  };
};
